import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { getAttendanceLogs, deleteAttendanceLog, downloadLogs } from "../services/attendanceService";
import { getShortHighlights, getActiveBreaks } from "../services/statisticsService";
import { secToHhmm, liveBreakSeconds, liveActualSeconds } from "../utils/liveHours";
import useConfirm from "../hooks/useConfirm";
import ConfirmModal from "../components/ui/ConfirmModal";
import EditEntryModal from "../components/attendance/EditEntryModal";
import { useBreakDetails, BreakCell, BreakDetailRow } from "../components/attendance/BreakDetails";
import { ShortfallTip } from "../components/attendance/HoursCell";
import Skeleton, { SkeletonRows } from "../components/ui/Skeleton";

const pad2 = (n) => (n == null ? "—" : String(n).padStart(2, "0"));
const fmtTime = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm a") : null);
// Overnight record: check-out happened on a later calendar date than check-in. Computed on
// the backend (DATE(checkout) > DATE(checkin)) since the frontend only receives time-only values.
const isOvernight = (l) => !!l.isOvernight;
const fmtHours = (h) => {
  if (!h) return null;
  const [hh, mm] = h.split(":");
  return `${hh}h ${mm}m`;
};
// Elapsed time since a break started, e.g. "23m" / "1h 5m".
const fmtElapsed = (start) => {
  if (!start) return "";
  const mins = Math.max(0, moment().diff(moment(start), "minutes"));
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};
const avatarFor = (log, size = 34) => {
  const cls = `av av-${size}`;
  return log.memberImgUrl ? (
    <img className={cls} src={log.memberImgUrl} alt="" style={{ objectFit: "cover" }} />
  ) : (
    <div className={`${cls} av-MH`}>{(log.memberName || "?").slice(0, 2).toUpperCase()}</div>
  );
};

const Dashboard = () => {
  const [highlights, setHighlights] = useState({});
  const [logs, setLogs] = useState([]);
  const [absentMembers, setAbsentMembers] = useState([]);
  const [onBreak, setOnBreak] = useState([]);
  const [lateThreshold, setLateThreshold] = useState(null); // "HH:mm:ss" late cutoff from settings
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // log open in the Edit-entry modal
  const breakDetails = useBreakDetails(); // expandable break/back breakdown per row
  const navigate = useNavigate();
  const goToProfile = (id) => id && navigate(`/members/${id}`);
  const handleDownload = () => downloadLogs(moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD"));
  const applyUpdatedLog = (updated) =>
    setLogs((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));

  // `silent` skips the skeleton flash so the background refresh (below) is invisible.
  const load = async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    const [h, attendance, breaks] = await Promise.all([
      getShortHighlights(),
      getAttendanceLogs(),
      getActiveBreaks(),
    ]);
    setHighlights(h ?? {});
    setLogs(attendance?.logs ?? []);
    setAbsentMembers(attendance?.absentMembers ?? []);
    setLateThreshold(attendance?.policy?.lateThreshold ?? null);
    setOnBreak(breaks?.onBreak ?? []);
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    load();
    // Silent poll: refreshes logs + on-break state and re-renders so the live break /
    // actual-hrs counters advance, and self-corrects when a member starts/ends a break.
    const t = setInterval(() => load({ silent: true }), 30000);
    return () => clearInterval(t);
  }, []);

  const lateLogs = useMemo(() => logs.filter((l) => l.isLate), [logs]);
  const lateCount = lateLogs.length;
  const lateLabel = fmtTime(lateThreshold) || "the cutoff"; // e.g. "09:15 am"
  // memberId -> open break start timestamp, for the live break/actual-hrs counters.
  const breakStartByMember = useMemo(() => new Map(onBreak.map((b) => [b.memberId, b.breakStart])), [onBreak]);
  const remoteCount = useMemo(() => logs.filter((l) => l.workType === "REMOTE").length, [logs]);
  const officeCount = logs.length - remoteCount;
  const remotePct = logs.length ? Math.round((remoteCount / logs.length) * 100) : 0;
  const officePct = logs.length ? 100 - remotePct : 0;

  const copyLate = async () => {
    const names = lateLogs.map((l) => l.memberDisplayName || l.memberName).join(", ");
    try {
      await navigator.clipboard.writeText(`Late check-ins (after ${lateLabel}): ${names}`);
      toast.success("Copied late list");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const { confirm, modalProps } = useConfirm();
  const handleDelete = async (log) => {
    const ok = await confirm({ title: "Delete attendance log?", message: "This permanently removes this check-in/out record." });
    if (!ok) return;
    const { status } = await deleteAttendanceLog(log.id);
    if (status === 200) {
      setLogs((prev) => prev.filter((l) => l.id !== log.id));
      toast.success("Log deleted");
    } else toast.error("Could not delete log");
  };

  const kpis = [
    { label: "Total members", value: pad2(highlights.totalMembers), sub: "in the workspace", color: "var(--brand)", tint: "var(--accent-tint)", icon: "users" },
    { label: "Check-ins", value: pad2(highlights.checkIns), sub: "present today", color: "#34D3A6", tint: "rgba(52,211,166,.16)", icon: "checkin" },
    { label: "Working remote", value: pad2(highlights.remoteCheckIns), sub: "of today's check-ins", color: "var(--remote)", tint: "var(--remote-bg)", icon: "wifi" },
    { label: "Late today", value: pad2(lateCount), sub: `after ${lateLabel}`, color: "var(--pink)", tint: "var(--pink-bg)", icon: "clock" },
  ];

  const scrollToLate = () => document.getElementById("late-card")?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <>
      {/* KPI cards */}
      <div className="kpis">
        {kpis.map((k, i) => (
          <div className="kpi" key={i}>
            <div className="kpi-stripe" style={{ background: k.color }} />
            <div className="kpi-head">
              <div className="kpi-label">{k.label}</div>
              <div className="kpi-icon" style={{ background: k.tint }}>
                <svg style={{ color: k.color }}><use href={`#i-${k.icon}`} /></svg>
              </div>
            </div>
            <div className="kpi-value">{loading ? <Skeleton width={54} height={30} radius={8} /> : k.value}</div>
            <div className="kpi-footer">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Late alert strip */}
      {!loading && lateCount > 0 && (
        <div className="alert-strip">
          <div className="lead">
            <span className="icon"><svg><use href="#i-clock" /></svg></span>
            <span className="text">
              <strong>{lateCount} member{lateCount > 1 ? "s" : ""}</strong> checked in after {lateLabel} today — consider reviewing office-hours policy.
            </span>
          </div>
          <button type="button" className="review" onClick={scrollToLate}>Review →</button>
        </div>
      )}

      {/* Log table + right rail */}
      <div className="dash-grid">
        {/* attendance log */}
        <div className="card">
          <div className="log-head">
            <div>
              <div className="log-title">Today's attendance log</div>
              <div className="log-sub">{logs.length} of {pad2(highlights.totalMembers)} members · live</div>
            </div>
            <button type="button" className="log-export" onClick={handleDownload}>
              <svg><use href="#i-download" /></svg>Export CSV
            </button>
          </div>

          {/* Header + rows scroll together on narrow screens. */}
          <div className="table-scroll">
          <div className="log-grid log-colhead">
            <div>Member</div><div>Check in</div><div>Check out</div><div>Actual hrs</div><div>Break</div><div>Type</div><div></div>
          </div>

          {loading && Array.from({ length: 8 }).map((_, i) => (
            <div className="log-grid log-row" key={`sk-${i}`} style={{ cursor: "default" }}>
              <div className="log-mem">
                <span className="skeleton skeleton-circle" style={{ width: 34, height: 34 }} />
                <div className="info" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Skeleton width={110} /><Skeleton width={70} />
                </div>
              </div>
              <div><Skeleton width={64} /></div><div><Skeleton width={64} /></div>
              <div><Skeleton width={70} /></div><div><Skeleton width={60} /></div><div><Skeleton width={64} /></div><div></div>
            </div>
          ))}

          {!loading && logs.length === 0 && (
            <div style={{ padding: "26px 20px", textAlign: "center", color: "var(--muted)" }}>No check-ins yet today.</div>
          )}

          {!loading && logs.map((log) => {
            const remote = log.workType === "REMOTE";
            const late = !!log.isLate;
            const missing = !log.checkout;
            // Live values: total break includes the still-open break; actual hrs ticks for an
            // in-progress session today (null -> fall back to the stored, checked-out value).
            const now = moment();
            const liveBreakSec = liveBreakSeconds(log, breakStartByMember.get(log.memberId), now);
            const liveActualSec = liveActualSeconds(log, liveBreakSec, now);
            const isLive = liveActualSec != null;
            const actual = isLive ? secToHhmm(liveActualSec) : fmtHours(log.actualWorkingHour || log.workingHour);
            // Undertime once the hours are final. Server-computed (rowShortfallSeconds), the
            // same rule behind the History "Work Shortfall" KPI, and 0 while a session is
            // still open — so a running shift keeps its normal colour and only a finished,
            // short day turns pink. Ranked above green/amber: falling short of the required
            // hours matters more than having arrived late.
            const short = !isLive && Number(log.shortfallSeconds) > 0;
            let aColor = "var(--muted)", aBg = "var(--gray-bg)";
            if (isLive && !late) { aColor = "var(--brand)"; aBg = "var(--accent-tint)"; }
            else if (short) { aColor = "var(--pink)"; aBg = "var(--pink-bg)"; }
            else if (actual && !missing && !late) { aColor = "var(--green)"; aBg = "var(--green-bg)"; }
            else if (late) { aColor = "var(--amber)"; aBg = "var(--amber-bg)"; }
            const brkOpen = breakDetails.isOpen(log.id);
            return (
              <React.Fragment key={log.id}>
              <div className="log-grid log-row" onClick={() => goToProfile(log.memberId)} role="button" tabIndex={0} title="View profile">
                <div className="log-mem">
                  {avatarFor(log)}
                  <div className="info">
                    <div className="log-name">{log.memberDisplayName || log.memberName}</div>
                    <div className="log-role">{log.memberTitle || ""}</div>
                  </div>
                </div>
                <div className="log-time">{fmtTime(log.checkIn) || "—"}</div>
                <div className="log-time">{log.checkout ? fmtTime(log.checkout) : "—"}{isOvernight(log) && (<span className="ovn-tip" title="Overnight shift — checked out the next day"><svg><use href="#i-info" /></svg></span>)}</div>
                <div><span className="pill-actual" style={{ color: aColor, background: aBg }}>{actual || "—"}</span>{short && <ShortfallTip seconds={log.shortfallSeconds} />}</div>
                <div className="log-break"><BreakCell log={log} breakStr={secToHhmm(liveBreakSec)} expanded={brkOpen} onToggle={breakDetails.toggle} /></div>
                <div>
                  <span className="pill-type" style={{ color: `var(--${remote ? "remote" : "office"})`, background: `var(--${remote ? "remote" : "office"}-bg)` }}>
                    <svg><use href={`#i-${remote ? "wifi" : "building"}`} /></svg>{remote ? "Remote" : "Office"}
                  </span>
                </div>
                <div>
                  <button type="button" className="row-edit" title="Edit entry" onClick={(e) => { e.stopPropagation(); setEditing(log); }}>
                    <svg><use href="#i-edit" /></svg>
                  </button>
                </div>
              </div>
              {brkOpen && <BreakDetailRow breaks={breakDetails.breaksById[log.id]} loading={breakDetails.loadingId === log.id} />}
              </React.Fragment>
            );
          })}
          </div>
        </div>

        {/* right rail */}
        <div className="rail">
          {/* work type breakdown */}
          <div className="rail-card">
            <div className="rail-title" style={{ marginBottom: 14 }}>Work type breakdown</div>
            {loading ? (
              <>
                <div style={{ marginBottom: 13 }}><Skeleton variant="line" height={7} /></div>
                <Skeleton variant="line" height={7} />
              </>
            ) : (
              <>
                <div className="wt-row">
                  <div className="wt-top"><span className="wt-label"><span className="wt-dot" style={{ background: "var(--office)" }} />Office</span><span className="wt-val">{officeCount}</span></div>
                  <div className="wt-track"><div className="wt-fill" style={{ width: `${officePct}%`, background: "var(--office)" }} /></div>
                </div>
                <div className="wt-row">
                  <div className="wt-top"><span className="wt-label"><span className="wt-dot" style={{ background: "var(--remote)" }} />Remote</span><span className="wt-val">{remoteCount}</span></div>
                  <div className="wt-track"><div className="wt-fill" style={{ width: `${remotePct}%`, background: "var(--remote)" }} /></div>
                </div>
              </>
            )}
          </div>

          {/* on break now */}
          <div className="rail-card">
            <div className="rail-head gap"><div className="rail-title">On break now</div><span className="brk-badge">{onBreak.length}</span></div>
            {loading ? (
              <SkeletonRows count={1} />
            ) : onBreak.length === 0 ? (
              <p className="rail-empty" style={{ margin: 0 }}>No one is on a break right now.</p>
            ) : (
              onBreak.map((b) => (
                <div
                  className="rail-person brk rail-person--link"
                  key={b.memberId}
                  role="button"
                  tabIndex={0}
                  title="View profile"
                  onClick={() => goToProfile(b.memberId)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToProfile(b.memberId)}
                >
                  {avatarFor(b, 38)}
                  <div className="info">
                    <div className="pname">{b.memberDisplayName || b.memberName}</div>
                    <div className="psub">Since {fmtTime(moment(b.breakStart).format("HH:mm:ss"))} · {fmtElapsed(b.breakStart)}</div>
                  </div>
                  <span className="brk-pill">break</span>
                </div>
              ))
            )}
          </div>

          {/* late check-ins */}
          <div className="rail-card" id="late-card">
            <div className="rail-head">
              <div className="rail-title">Late check-ins</div>
              {!loading && lateLogs.length > 0 && <button type="button" className="late-copy" onClick={copyLate}>Copy</button>}
            </div>
            {loading && <SkeletonRows count={2} />}
            {!loading && lateLogs.length === 0 && <div className="rail-empty">No late check-ins</div>}
            {!loading && lateLogs.length > 0 && (
              <div className="rail-scroll">
                {lateLogs.map((l) => (
                  <div className="rail-person" key={l.id}>
                    {avatarFor(l)}
                    <div className="info">
                      <div className="pname">{l.memberDisplayName || l.memberName}</div>
                      <div className="prole">{l.memberTitle || ""}</div>
                    </div>
                    <span className="late-time"><svg><use href="#i-clock" /></svg>{fmtTime(l.checkIn)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* absent today */}
          <div className="rail-card">
            <div className="rail-title" style={{ marginBottom: 13 }}>Absent today · {loading ? "…" : absentMembers.length}</div>
            {loading && <SkeletonRows count={3} />}
            {!loading && absentMembers.length === 0 && <div className="rail-empty">Everyone is checked in 🎉</div>}
            {!loading && absentMembers.length > 0 && (
              <div className="rail-scroll">
                {absentMembers.map((m) => (
                  <div
                    className="rail-person rail-person--link"
                    key={m.memberId}
                    role="button"
                    tabIndex={0}
                    title="View profile"
                    onClick={() => goToProfile(m.memberId)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToProfile(m.memberId)}
                  >
                    {m.memberImgUrl ? (
                      <img className="av av-34" src={m.memberImgUrl} alt="" style={{ objectFit: "cover" }} />
                    ) : (
                      <div className="av av-34 av-muted">{(m.memberName || "?").slice(0, 2).toUpperCase()}</div>
                    )}
                    <div className="info">
                      <div className="pname" style={{ color: "var(--text-2)" }}>{m.memberDisplayName || m.memberName}</div>
                      <div className="prole">{m.memberTitle || ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <EditEntryModal
          log={editing}
          onClose={() => setEditing(null)}
          onSaved={applyUpdatedLog}
          onDelete={(log) => { setEditing(null); handleDelete(log); }}
        />
      )}
      <ConfirmModal {...modalProps} />
    </>
  );
};

export default Dashboard;
